// Transfer credit — move balance between two user wallets (SM-149).
//
// The operator picks a source and destination user (autocomplete over
// /admin/users?q= — name / email / id), picks the CURRENCY the move is
// in, types an amount (Max + stepper + quick-add, capped by the source's
// available balance in that currency), writes a reason, and confirms in
// a summary modal. The server side is ONE atomic ledger transaction
// (TRANSFER debit on the source + TRANSFER credit on the destination) —
// a rejected transfer touches neither balance. Shares WALLET_CREDIT with
// the rest of the admin money-movement surface; both users get a bell
// notification and a History row from the ledger's flow events.
//
// SM-492 added the currency. This page was the last operator money form
// still assuming there was only one — it read one wallet per user, drew
// every figure with the GOLD-shaped SparkAmount renderer, and sent no
// currency at all, so the ledger's absent-reads-as-GOLD default decided
// what moved. That is the failure the epic's picker exists to prevent:
// the three currencies share a wire scale, so a transfer of the wrong
// one looks exactly like a transfer of the right one until someone
// reconciles. Everything the amount depends on — the balances, the cap,
// the stepper, the quick-add chips, the entry precision and the outcome
// preview — is now derived from the picked currency rather than from a
// module constant, and the picked currency travels on the wire.
//
// Deep-linkable: /treasury/transfer?from=<user_id>&to=<user_id>&currency=<code>
// pre-selects the parties and the currency (the wallet detail page links
// here, and it already knows which currency the operator was looking at).
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { APIError } from "@/lib/api";
import {
  fetchUsersByIDs,
  fetchWallet,
  keys,
  transferWallet,
} from "@/lib/queries";
import type { WalletItem, WalletTransferResult } from "@/lib/types";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { Input } from "@/components/ui/Input";
import { Skeleton } from "@/components/ui/Skeleton";
import { CurrencyAmount } from "@/components/ui/CurrencyAmount";
import { CurrencyMark } from "@/components/ui/CurrencyMark";
import {
  CurrencySelect,
  moneyMoveBlockReason,
} from "@/components/currency/CurrencySelect";
import { useCurrencyPresentation } from "@/components/currency/CurrencyPresentationProvider";
import { formatCurrencyText } from "@/currency";
import { rowCurrency } from "@/components/currency/columns";
import { UserPicker, type PickedUser } from "@/components/treasury/UserPicker";
import {
  isCurrencyCode,
  parseUnitToMinor,
  unitFromMinor,
  type CurrencyCode,
} from "@/lib/currency";
import { MAX_MOVE_UNITS, MAX_MOVE_MSG, sanitizeAmount } from "@/lib/moneyInput";
import { statusLabel } from "@/lib/format";
import { cn } from "@/lib/cn";

interface TransferSearch {
  from?: string;
  to?: string;
  currency?: CurrencyCode;
}

export const Route = createFileRoute("/_app/treasury/transfer")({
  validateSearch: (search: Record<string, unknown>): TransferSearch => ({
    from: typeof search.from === "string" && search.from !== "" ? search.from : undefined,
    to: typeof search.to === "string" && search.to !== "" ? search.to : undefined,
    // An unrecognised code is dropped rather than coerced: landing on a
    // currency the operator did not name is exactly the confusion the
    // picker exists to prevent, and the form's own default is honest.
    currency: isCurrencyCode(search.currency) ? search.currency : undefined,
  }),
  component: TransferPage,
});

// Stepper / quick-add increments, in whole DISPLAY units. They are
// converted through the picked currency's own unit at the point of use
// (`parseUnitToMinor`), never through a module-level divisor.
//
// They used to be pre-multiplied into minor units by the money module's
// own divisor — literal constants, correct only for as long as every
// currency shared one divisor. A literal divisor beside a labelled
// amount is how a relabel silently changes what a button does: SM-460
// moved the display to $1 units and left an earlier pair of these
// behind, which made the stepper move $0.10 per click on a control
// labelled "$10".
const STEP_UNITS = 10;
const QUICK_ADD_UNITS = [100, 1_000, 10_000];

/**
 * A wire minor value rendered back into the text input's display format.
 *
 * Built from the currency's own unit and then run through the field's
 * own sanitizer, so what the Max button writes is something the operator
 * could have typed: ASCII digits and at most `dp` decimals, no locale
 * grouping (`formatSpark`'s toLocaleString separators would corrupt the
 * round-trip through sanitizeAmount in e.g. de-DE).
 *
 * The sanitizer TRUNCATES the tail rather than rounding it, which is the
 * direction that matters here: a balance of 1.2345 at 2dp becomes 1.23,
 * so Max can never write an amount larger than what is available.
 */
function amountInputValue(minor: number, code: CurrencyCode, dp: number): string {
  if (minor <= 0) return "";
  return sanitizeAmount(unitFromMinor(minor, code), dp);
}

/**
 * A loaded wallet's currency as a canonical code, or undefined when
 * there is no wallet to disagree with yet.
 *
 * `rowCurrency` is the console's one answer to "what currency is this
 * row in", CREDIT/SPARK aliases included; the alternative — comparing
 * the raw wire string — reads legacy GOLD as a fourth currency.
 */
function walletCurrency(item: WalletItem | undefined): string | undefined {
  return item ? rowCurrency(item.currency) : undefined;
}

function TransferPage() {
  const search = Route.useSearch();
  const qc = useQueryClient();

  const [fromUser, setFromUser] = useState<PickedUser | null>(null);
  const [toUser, setToUser] = useState<PickedUser | null>(null);
  // GOLD by default: it is the currency every transfer moved before this
  // epic, so an operator who ignores the picker gets the behaviour they
  // have always had (SM-469's rule for the adjustment card, applied to
  // the one form that had not adopted it).
  const [currency, setCurrency] = useState<CurrencyCode>(search.currency ?? "GOLD");
  const [amount, setAmount] = useState(""); // display units (e.g. "12.50")
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState<{
    result: WalletTransferResult;
    from: PickedUser;
    to: PickedUser;
    amountMinor: string;
    currency: CurrencyCode;
  } | null>(null);

  // The picked currency's configured identity — name, symbol, artwork
  // and DISPLAY PRECISION. The last of these drives the amount field, so
  // an operator configuring GOLD to 0dp on /currencies gets a whole-unit
  // entry box here rather than a box that accepts cents the ledger would
  // then have to round.
  const presentation = useCurrencyPresentation(currency);
  const dp = presentation.decimals;

  // Hydrate the pickers from ?from= / ?to= exactly once — after that
  // the operator's own picks win.
  const hydrated = useRef(false);
  const hydrateIDs = [search.from, search.to].filter((v): v is string => !!v);
  const hydrate = useQuery({
    queryKey: ["transfer-party-hydrate", search.from ?? "", search.to ?? ""],
    queryFn: () => fetchUsersByIDs(hydrateIDs),
    enabled: !hydrated.current && hydrateIDs.length > 0,
  });
  useEffect(() => {
    if (hydrated.current || !hydrate.data) return;
    hydrated.current = true;
    const byID = new Map(hydrate.data.map((u) => [u.user_id, u]));
    const pick = (id?: string): PickedUser | null => {
      if (!id) return null;
      const u = byID.get(id);
      // An id the lookup can't resolve (or a soft-deleted CLOSED
      // account) stays unpicked — a bare-id fallback would let a stale
      // or typoed deep link send funds to a nonexistent user, which the
      // ledger would happily lazy-create an unreachable wallet for.
      if (!u || u.status === "CLOSED") return null;
      return { user_id: u.user_id, display_name: u.display_name, email: u.email, avatar_url: u.avatar_url };
    };
    setFromUser((prev) => prev ?? pick(search.from));
    setToUser((prev) => prev ?? pick(search.to));
  }, [hydrate.data, search.from, search.to]);

  // Balance panels, per (user, currency). The currency is part of the
  // query key, so changing it refetches both sides rather than showing
  // one currency's balance under another's name. A 404 means "no ledger
  // activity yet in THIS currency" — a valid destination (the wallet
  // lazy-creates on first credit), a doomed source (nothing to move).
  const fromWallet = useWalletFor(fromUser?.user_id, currency);
  const toWallet = useWalletFor(toUser?.user_id, currency);

  // Idempotency key, minted once per confirm session (when the modal
  // opens). A retried Transfer click after a lost response replays the
  // same operation server-side instead of moving the money twice; a
  // fresh review mints a fresh key.
  const opID = useRef<string>("");

  const transfer = useMutation({
    mutationFn: (input: {
      from: string;
      to: string;
      amountMinor: string;
      reason: string;
      opID: string;
      currency: CurrencyCode;
    }) =>
      transferWallet(input.from, input.to, input.amountMinor, input.reason, input.opID, input.currency),
    onSuccess: (result, input) => {
      setConfirming(false);
      setDone({
        result,
        from: fromUser!,
        to: toUser!,
        amountMinor: input.amountMinor,
        currency: input.currency,
      });
      setAmount("");
      setReason("");
      qc.invalidateQueries({ queryKey: keys.treasury });
      // The blast radius is the USER, not one currency: the wallets list
      // and the balances strip both show every currency they hold.
      qc.invalidateQueries({ queryKey: keys.walletDetailAll(input.from) });
      qc.invalidateQueries({ queryKey: keys.walletDetailAll(input.to) });
    },
    meta: { successMessage: "Transfer completed." },
  });

  const amountMinorNum = parseUnitToMinor(amount, currency);
  const amountMinor = String(amountMinorNum); // wire value (int64 minor string)
  const amountNum = Number(amount) || 0;

  const availableMinor = Number(fromWallet.item?.available ?? 0) || 0;
  // The per-operation ceiling is expressed in whole display units, so it
  // has to be converted through the picked currency's unit like any other
  // typed amount. It used to be multiplied by a literal 100 — the
  // pre-SM-460 divisor — which capped Max and the stepper at a hundredth
  // of the limit the error message beside them states.
  const capMinor = parseUnitToMinor(String(MAX_MOVE_UNITS), currency);
  const maxMinor = Math.min(availableMinor, capMinor);

  const bothPicked = !!fromUser && !!toUser;
  // The pickers exclude the other side's pick, but ?from=/?to= deep
  // links can still land the same user on both sides.
  const sameParty = bothPicked && fromUser!.user_id === toUser!.user_id;
  const overAvailable = amountMinorNum > availableMinor;
  const overMax = amountNum > MAX_MOVE_UNITS;
  const fromFrozen = !!fromWallet.item && fromWallet.item.status !== "ACTIVE";
  const toClosed = toWallet.item?.status === "CLOSED";
  // Both legs must be the currency the operator picked. The wallets are
  // READ by currency so a mismatch should be impossible — which is
  // exactly why it is worth catching here rather than discovering it as
  // a moved balance: a wallet answering in another currency means the
  // read and the write disagree about what is about to move.
  //
  // Normalised through `rowCurrency` first, because the ledger still
  // answers GOLD as the legacy CREDIT alias on some reads. Comparing the
  // raw wire strings would make every GOLD transfer look cross-currency
  // and block the one currency this page has always moved.
  const currencyBlock =
    moneyMoveBlockReason("transfer", currency, walletCurrency(fromWallet.item)) ??
    moneyMoveBlockReason("transfer", currency, walletCurrency(toWallet.item));
  const canReview =
    bothPicked &&
    !sameParty &&
    !fromWallet.loading &&
    !toWallet.loading &&
    amountMinorNum > 0 &&
    !overAvailable &&
    !overMax &&
    !fromFrozen &&
    !toClosed &&
    !currencyBlock &&
    reason.trim() !== "";

  const bump = (deltaUnits: number) => {
    const delta = parseUnitToMinor(String(Math.abs(deltaUnits)), currency);
    const next = Math.max(0, Math.min(amountMinorNum + (deltaUnits < 0 ? -delta : delta), maxMinor));
    setAmount(amountInputValue(next, currency, dp));
  };

  const swap = () => {
    setFromUser(toUser);
    setToUser(fromUser);
    setConfirming(false);
    transfer.reset();
  };

  // Changing the currency re-reads both balances (the query key carries
  // it) and re-validates the amount already typed against the new
  // sender's balance — it does NOT clear the amount, because the
  // operator's intent ("move 500") usually survives the change and a
  // silently emptied field reads as a bug. What it must not survive is
  // an open review: a confirmation naming the old currency is worse than
  // no confirmation at all.
  const pickCurrency = (next: CurrencyCode) => {
    if (next === currency) return;
    setCurrency(next);
    setConfirming(false);
    transfer.reset();
  };

  const reset = () => {
    setAmount("");
    setReason("");
    setDone(null);
    setConfirming(false);
    transfer.reset();
  };

  const label = (u: PickedUser | null) =>
    u ? u.display_name || u.email || u.user_id : "";

  const stepLabel = (minor: number) =>
    formatCurrencyText(presentation, minor, { trimZeros: true });

  // A digit template ("0.00"), not copy — built here rather than inline
  // so the page-copy extractor does not lift `0.{{value1}}` into the
  // translation catalogue as though it were a sentence.
  const amountPlaceholder = dp > 0 ? `0.${"0".repeat(dp)}` : "0";

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <Link to="/treasury" className="text-2xs text-muted hover:text-fg">
          ← back to treasury
        </Link>
        <h1 className="mt-1 font-display text-2xl tracking-tight">Transfer credit</h1>
        <p className="mt-1 text-xs text-muted">
          Move funds from one user's wallet to another's, in one currency. The
          move is one atomic ledger transaction — both users get a notification
          and an activity-log entry, and the transfer is recorded in the audit
          log against the currency it moved.
        </p>
      </div>

      {done && (
        <Card className="border-yes/40">
          <CardBody className="space-y-3 pt-5">
            <div className="flex items-center gap-2">
              <span className="grid h-6 w-6 place-items-center rounded-full bg-yes/15 text-yes">
                <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2.5}>
                  <path d="M3.5 8.5l3 3 6-7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <p className="text-sm font-semibold text-yes">Transfer completed</p>
            </div>
            <p className="text-xs text-t-2">
              <CurrencyAmount currency={done.currency} value={done.amountMinor} className="font-medium" />{" "}
              moved from{" "}
              <span className="font-medium text-t-1">{label(done.from)}</span> to{" "}
              <span className="font-medium text-t-1">{label(done.to)}</span>.
            </p>
            <div className="grid gap-2 text-2xs text-muted sm:grid-cols-2">
              <span>
                {label(done.from)} now has{" "}
                <CurrencyAmount currency={done.currency} value={done.result.from_available} />
              </span>
              <span>
                {label(done.to)} now has{" "}
                <CurrencyAmount currency={done.currency} value={done.result.to_available} />
              </span>
            </div>
            <p className="font-mono text-2xs text-t-4">transfer {done.result.transfer_id}</p>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Parties</CardTitle>
        </CardHeader>
        <CardBody className="space-y-5 pt-5">
          {/* The currency comes FIRST: it decides what every balance
              below it means, and an operator who reads the balances
              before naming the currency has read the wrong numbers. */}
          <CurrencySelect
            action="transfer"
            id="transfer-currency"
            value={currency}
            onChange={pickCurrency}
          />

          <div className="grid gap-4 lg:grid-cols-[1fr_auto_1fr] lg:items-start">
            <div className="space-y-3">
              <UserPicker
                label="From"
                selected={fromUser}
                onSelect={(u) => {
                  setFromUser(u);
                  setConfirming(false);
                  transfer.reset();
                }}
                excludeIDs={toUser ? [toUser.user_id] : []}
              />
              {fromUser && <WalletPanel state={fromWallet} role="from" currency={currency} />}
            </div>

            <div className="flex justify-center lg:pt-7">
              <button
                type="button"
                onClick={swap}
                disabled={!fromUser && !toUser}
                title="Swap sender and recipient"
                aria-label="Swap sender and recipient"
                className="grid h-9 w-9 place-items-center rounded-full border border-line-1 bg-bg-2 text-t-3 transition-colors hover:border-line-3 hover:text-t-1 disabled:opacity-40"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3 4 7l4 4" />
                  <path d="M4 7h16" />
                  <path d="m16 21 4-4-4-4" />
                  <path d="M20 17H4" />
                </svg>
              </button>
            </div>

            <div className="space-y-3">
              <UserPicker
                label="To"
                selected={toUser}
                onSelect={(u) => {
                  setToUser(u);
                  setConfirming(false);
                  transfer.reset();
                }}
                excludeIDs={fromUser ? [fromUser.user_id] : []}
              />
              {toUser && <WalletPanel state={toWallet} role="to" currency={currency} />}
            </div>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Amount</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4 pt-5">
          <div className="space-y-1.5">
            <div className="flex items-end gap-2">
              <div className="grow">
                <Input
                  label={`Amount in ${presentation.displayName}`}
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(sanitizeAmount(e.target.value, dp))}
                  placeholder={amountPlaceholder}
                  disabled={!bothPicked}
                  error={
                    overAvailable
                      ? `Exceeds sender's available ${presentation.displayName}`
                      : overMax
                        ? MAX_MOVE_MSG
                        : undefined
                  }
                />
              </div>
              <div className="flex gap-1 pb-[1px]">
                <StepButton
                  label={`Decrease by ${stepLabel(parseUnitToMinor(String(STEP_UNITS), currency))}`}
                  onClick={() => bump(-STEP_UNITS)}
                  disabled={!bothPicked || amountMinorNum <= 0}
                >
                  −
                </StepButton>
                <StepButton
                  label={`Increase by ${stepLabel(parseUnitToMinor(String(STEP_UNITS), currency))}`}
                  onClick={() => bump(+STEP_UNITS)}
                  disabled={!bothPicked || amountMinorNum >= maxMinor}
                >
                  +
                </StepButton>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 px-0.5 text-2xs">
              <div className="flex flex-wrap gap-1.5">
                {QUICK_ADD_UNITS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    disabled={!bothPicked || amountMinorNum >= maxMinor}
                    onClick={() => bump(s)}
                    className="inline-flex items-center gap-1 rounded-full border border-line-1 bg-bg-2 px-2 py-0.5 font-medium text-t-2 transition-colors hover:border-line-3 hover:text-t-1 disabled:opacity-40"
                  >
                    <span aria-hidden>+</span>
                    <CurrencyMark presentation={presentation} />
                    {s.toLocaleString()}
                  </button>
                ))}
              </div>
              <button
                type="button"
                disabled={!bothPicked || maxMinor <= 0}
                onClick={() => setAmount(amountInputValue(maxMinor, currency, dp))}
                className="inline-flex items-center gap-1 font-medium text-neon hover:text-neon-2 disabled:opacity-40"
              >
                Max <CurrencyAmount currency={currency} value={String(maxMinor)} />
              </button>
            </div>
          </div>

          {/* Live outcome preview — what each side ends up with. Waits
              for both wallet queries so it never previews against a
              still-loading (0) destination balance. */}
          {bothPicked && !sameParty && !fromWallet.loading && !toWallet.loading &&
            amountMinorNum > 0 && !overAvailable && !overMax && (
            <div className="grid gap-2 rounded-md border border-line-1 bg-bg-2 px-3 py-2.5 text-2xs sm:grid-cols-2">
              <span className="text-muted">
                {label(fromUser)}{" "}
                <CurrencyAmount currency={currency} value={`-${amountMinor}`} tone="auto" sign className="font-medium" />{" "}
                → <CurrencyAmount currency={currency} value={String(Math.max(0, availableMinor - amountMinorNum))} />
              </span>
              <span className="text-muted">
                {label(toUser)}{" "}
                <CurrencyAmount currency={currency} value={amountMinor} tone="auto" sign className="font-medium" />{" "}
                → <CurrencyAmount currency={currency} value={String((Number(toWallet.item?.available ?? 0) || 0) + amountMinorNum)} />
              </span>
            </div>
          )}

          <Input
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={!bothPicked}
            placeholder="support ticket #1234 / balance correction"
            hint="Recorded in the audit log and shown on both users' activity rows."
          />

          {sameParty && (
            <p className="text-2xs text-warning">
              Sender and recipient are the same user — pick two different users.
            </p>
          )}
          {fromFrozen && (
            <p className="text-2xs text-warning">
              Sender's wallet is {statusLabel(fromWallet.item?.status)} — unfreeze it to transfer.
            </p>
          )}
          {toClosed && (
            <p className="text-2xs text-warning">Recipient's wallet is closed and cannot receive credit.</p>
          )}
          {currencyBlock && <p className="text-2xs text-warning">{currencyBlock}</p>}
          {transfer.isError && (
            <p className="text-2xs text-danger">{(transfer.error as Error).message}</p>
          )}

          <div className="flex gap-2">
            <Button
              onClick={() => {
                opID.current = crypto.randomUUID();
                setConfirming(true);
              }}
              disabled={!canReview}
            >
              Review transfer
            </Button>
            {(amount || reason || done) && (
              <Button variant="ghost" onClick={reset}>
                Clear
              </Button>
            )}
          </div>
        </CardBody>
      </Card>

      <ConfirmModal
        open={confirming}
        title="Confirm transfer"
        description={
          // Whole sentences in one text node each: the console's
          // localisation bridge translates a DOM text node at a time, so
          // a sentence assembled from JSX children extracts as
          // untranslatable scraps ("from", "to", "Reason:").
          <div className="space-y-2">
            <p className="flex flex-wrap items-center gap-1.5 text-sm font-semibold text-t-1">
              <CurrencyMark presentation={presentation} size={18} />
              {formatCurrencyText(presentation, amountMinor)}
            </p>
            <p className="text-t-2">
              {`Move this amount from ${label(fromUser)} to ${label(toUser)}?`}
            </p>
            <p className="text-t-2">{`Reason: ${reason.trim()}`}</p>
            <p>
              {`This debits the sender's ${presentation.displayName} and credits the recipient's immediately; both users are notified, and no other currency is touched.`}
            </p>
          </div>
        }
        confirmLabel="Transfer"
        loading={transfer.isPending}
        error={transfer.isError ? (transfer.error as Error).message : undefined}
        onConfirm={() =>
          transfer.mutate({
            from: fromUser!.user_id,
            to: toUser!.user_id,
            amountMinor,
            reason: reason.trim(),
            opID: opID.current,
            currency,
          })
        }
        onCancel={() => {
          if (!transfer.isPending) setConfirming(false);
        }}
      />
    </div>
  );
}

// --- wallet balance panel ---

interface WalletPanelState {
  item?: WalletItem;
  loading: boolean;
  /** True when the user has no ledger activity yet (404). */
  missing: boolean;
  error?: string;
}

// useWalletFor loads the balance panel for one side, in ONE currency.
// 404 is a normal state ("no wallet in this currency yet"), not an error
// — a user who has never held RED has no RED wallet until something
// credits one.
function useWalletFor(userID: string | undefined, currency: CurrencyCode): WalletPanelState {
  const q = useQuery({
    queryKey: keys.walletDetail(userID ?? "", currency),
    queryFn: () => fetchWallet(userID!, currency),
    enabled: !!userID,
    retry: (count, err) => !(err instanceof APIError && err.status === 404) && count < 2,
  });
  const missing = q.error instanceof APIError && q.error.status === 404;
  return {
    item: q.data,
    loading: !!userID && q.isLoading,
    missing,
    error: q.error && !missing ? (q.error as Error).message : undefined,
  };
}

function WalletPanel({
  state,
  role,
  currency,
}: {
  state: WalletPanelState;
  role: "from" | "to";
  currency: CurrencyCode;
}) {
  if (state.loading) return <Skeleton className="h-16" />;
  if (state.error) {
    return (
      <p className="rounded-md border border-line-1 bg-bg-2 px-3 py-2.5 text-2xs text-danger">
        Couldn't load wallet: {state.error}
      </p>
    );
  }
  if (state.missing) {
    return (
      <div className="rounded-md border border-line-1 bg-bg-2 px-3 py-2.5">
        <p className="text-2xs text-muted">
          No wallet yet — available <CurrencyAmount currency={currency} value="0" />
          {role === "to" && " (created on first credit)"}
        </p>
      </div>
    );
  }
  if (!state.item) return null;
  const w = state.item;
  const active = w.status === "ACTIVE";
  return (
    <div className="space-y-1.5 rounded-md border border-line-1 bg-bg-2 px-3 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-2xs uppercase tracking-wide text-t-4">Available</span>
        <Badge tone={active ? "success" : "danger"}>{statusLabel(w.status)}</Badge>
      </div>
      <p className={cn("num text-sm font-semibold", role === "from" && !active && "text-danger")}>
        <CurrencyAmount currency={w.currency || currency} value={w.available} />
      </p>
      {Number(w.reserved) > 0 && (
        <p className="text-2xs text-muted">
          + <CurrencyAmount currency={w.currency || currency} value={w.reserved} tone="warning" /> reserved (not transferable)
        </p>
      )}
    </div>
  );
}

// --- small stepper button ---

function StepButton({
  children,
  onClick,
  disabled,
  label,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className="grid h-10 w-10 place-items-center rounded-md border border-line-1 bg-bg-2 text-base font-semibold text-t-2 transition-colors hover:border-line-3 hover:text-t-1 disabled:opacity-40"
    >
      {children}
    </button>
  );
}
